import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Calendar, TrendingUp, Percent, ArrowDownToLine } from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Newsletter } from '../components/Newsletter';
import { FinancialChart } from '../components/Chart';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

interface RetirementData {
  year: number;
  age: number;
  startBalance: number;
  contribution: number;
  earnings: number;
  endBalance: number;
  inflationAdjusted: number;
}

const RetirementCalculator = () => {
  const [currentAge, setCurrentAge] = useState<number>(30);
  const [retirementAge, setRetirementAge] = useState<number>(65);
  const [currentSavings, setCurrentSavings] = useState<number>(50000);
  const [monthlyContribution, setMonthlyContribution] = useState<number>(500);
  const [returnRate, setReturnRate] = useState<number>(7);
  const [inflationRate, setInflationRate] = useState<number>(2);
  const [projections, setProjections] = useState<RetirementData[]>([]);

  const calculateRetirement = () => {
    const years = retirementAge - currentAge;
    const monthlyRate = returnRate / 100 / 12;
    const inflationFactor = 1 + (inflationRate / 100);
    
    let projectionData: RetirementData[] = [];
    let balance = currentSavings;
    
    for (let year = 0; year <= years; year++) {
      const startBalance = balance;
      const yearlyContribution = monthlyContribution * 12;
      const earnings = (balance + yearlyContribution) * (returnRate / 100);
      
      balance = balance + yearlyContribution + earnings;
      
      projectionData.push({
        year: currentAge + year,
        age: currentAge + year,
        startBalance: startBalance,
        contribution: yearlyContribution,
        earnings: earnings,
        endBalance: balance,
        inflationAdjusted: balance / Math.pow(inflationFactor, year)
      });
    }
    
    setProjections(projectionData);
  };

  useEffect(() => {
    calculateRetirement();
  }, [currentAge, retirementAge, currentSavings, monthlyContribution, returnRate, inflationRate]);

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    doc.setFontSize(20);
    doc.text('Retirement Projection Report', 14, 22);
    
    doc.setFontSize(12);
    doc.text(`Current Age: ${currentAge}`, 14, 32);
    doc.text(`Retirement Age: ${retirementAge}`, 14, 40);
    doc.text(`Current Savings: $${currentSavings.toLocaleString()}`, 14, 48);
    doc.text(`Monthly Contribution: $${monthlyContribution.toLocaleString()}`, 14, 56);
    doc.text(`Expected Return Rate: ${returnRate}%`, 14, 64);
    doc.text(`Inflation Rate: ${inflationRate}%`, 14, 72);
    
    const tableData = projections.map(row => [
      row.age,
      `$${row.startBalance.toLocaleString()}`,
      `$${row.contribution.toLocaleString()}`,
      `$${row.earnings.toLocaleString()}`,
      `$${row.endBalance.toLocaleString()}`,
      `$${row.inflationAdjusted.toLocaleString()}`
    ]);
    
    (doc as any).autoTable({
      head: [['Age', 'Start Balance', 'Contribution', 'Earnings', 'End Balance', 'Inflation Adj.']],
      body: tableData,
      startY: 80,
      theme: 'grid',
      styles: { fontSize: 8 }
    });
    
    doc.save('retirement-projection.pdf');
  };

  const chartData = {
    labels: projections.map(p => `Age ${p.age}`),
    datasets: [
      {
        label: 'Portfolio Value',
        data: projections.map(p => p.endBalance),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: true,
        tension: 0.4
      },
      {
        label: 'Inflation Adjusted',
        data: projections.map(p => p.inflationAdjusted),
        borderColor: '#34d399',
        backgroundColor: 'rgba(52, 211, 153, 0.1)',
        fill: true,
        tension: 0.4
      }
    ]
  };

  return (
    <div className="min-h-screen bg-[var(--background)] p-6">
      <div className="max-w-7xl mx-auto">
        <Navigation />
        
        <div className="mb-8">
          <Newsletter 
            title="Plan Your Retirement Better"
            subtitle="Subscribe to receive expert retirement strategies, investment advice, and market updates."
          />
        </div>

        <div className="glass-card p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <Calculator className="w-6 h-6 text-emerald-500" />
            </div>
            <h2 className="text-2xl font-semibold">Retirement Calculator</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Current Age</label>
              <div className="relative">
                <Calendar className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={currentAge}
                  onChange={(e) => setCurrentAge(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                  max="100"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Retirement Age</label>
              <div className="relative">
                <Calendar className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={retirementAge}
                  onChange={(e) => setRetirementAge(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min={currentAge}
                  max="100"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Current Savings</label>
              <div className="relative">
                <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={currentSavings}
                  onChange={(e) => setCurrentSavings(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Monthly Contribution</label>
              <div className="relative">
                <DollarSign className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={monthlyContribution}
                  onChange={(e) => setMonthlyContribution(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Expected Return Rate (%)</label>
              <div className="relative">
                <TrendingUp className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={returnRate}
                  onChange={(e) => setReturnRate(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                  max="20"
                  step="0.1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm text-slate-400">Inflation Rate (%)</label>
              <div className="relative">
                <Percent className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="number"
                  value={inflationRate}
                  onChange={(e) => setInflationRate(Number(e.target.value))}
                  className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  min="0"
                  max="10"
                  step="0.1"
                />
              </div>
            </div>
          </div>

          <button
            onClick={exportToPDF}
            className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors"
          >
            Export to PDF
          </button>
        </div>

        <div className="glass-card p-8 mb-8">
          <h3 className="text-xl font-semibold mb-6">Portfolio Growth Projection</h3>
          <FinancialChart
            type="line"
            data={chartData}
            height={400}
            className="mb-8"
          />
        </div>

        <div className="glass-card p-8 mb-8">
          <h3 className="text-xl font-semibold mb-6">Retirement Projections</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left p-3">Age</th>
                  <th className="text-right p-3">Start Balance</th>
                  <th className="text-right p-3">Contribution</th>
                  <th className="text-right p-3">Earnings</th>
                  <th className="text-right p-3">End Balance</th>
                  <th className="text-right p-3">Inflation Adjusted</th>
                </tr>
              </thead>
              <tbody>
                {projections.map((row, index) => (
                  <tr key={index} className="border-b border-slate-800 hover:bg-slate-800/30">
                    <td className="p-3">{row.age}</td>
                    <td className="text-right p-3">${row.startBalance.toLocaleString()}</td>
                    <td className="text-right p-3">${row.contribution.toLocaleString()}</td>
                    <td className="text-right p-3">${row.earnings.toLocaleString()}</td>
                    <td className="text-right p-3">${row.endBalance.toLocaleString()}</td>
                    <td className="text-right p-3">${row.inflationAdjusted.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8">
          <Newsletter 
            title="Secure Your Retirement"
            subtitle="Join our community to receive personalized retirement planning strategies and expert insights."
          />
        </div>
      </div>
    </div>
  );
};

export default RetirementCalculator;